import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  analyzePortfolio as analyzePortfolioAPI,
  comparePortfolio as comparePortfolioAPI,
  getOptimalPortfolio as getOptimalPortfolioAPI,
  getUserPortfolio as getUserPortfolioAPI,
  createUserPortfolio as createUserPortfolioAPI,
  updateUserPortfolio as updateUserPortfolioAPI,
  fetchPublic
} from '../api/portfolio';

// Thunks

export const fetchPublicScore = createAsyncThunk(
  'portfolio/fetchPublicScore',
    async ({ email, portfolio }, { rejectWithValue }) => {
      
      try {
        return await fetchPublic({email,portfolio});
      } catch (error) {
        return rejectWithValue(error.message);
      }
    }
  );
  

export const fetchPortfolio = createAsyncThunk(
  'portfolio/fetch',
  async (_, { rejectWithValue }) => {
    try {
      return await getUserPortfolioAPI();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);



export const createPortfolio = createAsyncThunk(
  'portfolio/create',
  async (portfolioData, { rejectWithValue }) => {
    try {
      return await createUserPortfolioAPI(portfolioData);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updatePortfolio = createAsyncThunk(
  'portfolio/update',
  async (portfolioData, { rejectWithValue }) => {
    try {
      return await updateUserPortfolioAPI(portfolioData);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const analyzePortfolio = createAsyncThunk(
  'portfolio/analyze',
  async (portfolioData, { rejectWithValue }) => {
    try {
      const result = await analyzePortfolioAPI(portfolioData);
      return {
        portfolio: portfolioData.portfolio,
        analysis: result
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const compareWithOptimal = createAsyncThunk(
  'portfolio/compare',
  async (payload, { rejectWithValue }) => {
    try {
      return await comparePortfolioAPI(payload);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOptimalPortfolio = createAsyncThunk(
  'portfolio/fetchOptimal',
  async (_, { rejectWithValue }) => {
    try {
      return await getOptimalPortfolioAPI();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  data: null,
  analysis: null,
  comparison: null,
  optimal: null,
  tempPortfolio: {},

  fetchPortfolioStatus: 'idle',
  fetchPortfolioError: null,

  createPortfolioStatus: 'idle',
  createPortfolioError: null,

  updatePortfolioStatus: 'idle',
  updatePortfolioError: null,

  analyzePortfolioStatus: 'idle',
  analyzePortfolioError: null,

  compareWithOptimalStatus: 'idle',
  compareWithOptimalError: null,

  fetchOptimalStatus: 'idle',
  fetchOptimalError: null,

  lastAnalyzed: null
};


const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {
    clearPortfolioData: (state) => {
      return initialState;
    },
    setTempPortfolio(state, action) {
      state.tempPortfolio = action.payload; 
    },
  },
  extraReducers: (builder) => {

    // fetchPortfolio
    builder
      .addCase(fetchPortfolio.pending, (state) => {
        state.fetchPortfolioStatus = 'loading'
        state.fetchPortfolioError = null
      })
      .addCase(fetchPortfolio.fulfilled, (state, action) => {
        state.fetchPortfolioStatus = 'succeeded'
        state.data = action.payload
      })
      .addCase(fetchPortfolio.rejected, (state, action) => {
        state.fetchPortfolioStatus = 'failed'
        state.fetchPortfolioError = action.payload
      })
  
    // createPortfolio
    builder
      .addCase(createPortfolio.pending, (state) => {
        state.createPortfolioStatus = 'loading'
        state.createPortfolioError = null
      })
      .addCase(createPortfolio.fulfilled, (state, action) => {
        state.createPortfolioStatus = 'succeeded'
        state.data = action.payload
      })
      .addCase(createPortfolio.rejected, (state, action) => {
        state.createPortfolioStatus = 'failed'
        state.createPortfolioError = action.payload
      })
  
    // updatePortfolio
    builder
      .addCase(updatePortfolio.pending, (state) => {
        state.updatePortfolioStatus = 'loading'
        state.updatePortfolioError = null
      })
      .addCase(updatePortfolio.fulfilled, (state, action) => {
        state.updatePortfolioStatus = 'succeeded'
        state.data = action.payload
      })
      .addCase(updatePortfolio.rejected, (state, action) => {
        state.updatePortfolioStatus = 'failed'
        state.updatePortfolioError = action.payload
      })
  
    // analyzePortfolio
    builder
      .addCase(analyzePortfolio.pending, (state) => {
        state.analyzePortfolioStatus = 'loading'
        state.analyzePortfolioError = null
      })
      .addCase(analyzePortfolio.fulfilled, (state, action) => {
        state.analyzePortfolioStatus = 'succeeded'
        state.analysis = action.payload.analysis
        state.lastAnalyzed = new Date().toISOString()
      })
      .addCase(analyzePortfolio.rejected, (state, action) => {
        state.analyzePortfolioStatus = 'failed'
        state.analyzePortfolioError = action.payload
      })
  
    // compareWithOptimal
    builder
      .addCase(compareWithOptimal.pending, (state) => {
        state.compareWithOptimalStatus = 'loading'
        state.compareWithOptimalError = null
      })
      .addCase(compareWithOptimal.fulfilled, (state, action) => {
        state.compareWithOptimalStatus = 'succeeded'
        state.comparison = action.payload
      })
      .addCase(compareWithOptimal.rejected, (state, action) => {
        state.compareWithOptimalStatus = 'failed'
        state.compareWithOptimalError = action.payload
      })
  
    // fetchOptimal
    builder
      .addCase(fetchOptimalPortfolio.pending, (state) => {
        state.fetchOptimalStatus = 'loading'
        state.fetchOptimalError = null
      })
      .addCase(fetchOptimalPortfolio.fulfilled, (state, action) => {
        state.fetchOptimalStatus = 'succeeded'
        state.optimal = action.payload
      })
      .addCase(fetchOptimalPortfolio.rejected, (state, action) => {
        state.fetchOptimalStatus = 'failed'
        state.fetchOptimalError = action.payload
      })
  }
  
});

export const { clearPortfolioData, setTempPortfolio } = portfolioSlice.actions;
export default portfolioSlice.reducer;